import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { TrendingUp, TrendingDown, Star, Loader2, Lock } from 'lucide-react';
import { SignalBadge } from '../common/Badge';
import { StockLogo } from '../common/StockLogo';
import { formatCurrency, formatNumber } from '../../utils/format';
import { useWatchlist } from '../../hooks/useWatchlist';
import type { StockSignal } from '../../types';

interface StockTableProps {
  stocks: StockSignal[];
  title: string;
  type: 'buy' | 'sell';
  loading?: boolean;
}

type SortField = 'rank' | 'disconnect_score';
type SortDirection = 'asc' | 'desc';

export function StockTable({ stocks, title, type, loading = false }: StockTableProps) {
  const navigate = useNavigate();
  const [sortField] = useState<SortField>('rank');
  const [sortDirection] = useState<SortDirection>('asc');
  const [togglingTicker, setTogglingTicker] = useState<string | null>(null);

  const { isInWatchlist, toggleTicker, hasWatchlistAccess } = useWatchlist();

  const sortedStocks = [...stocks].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    }
    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
  });

  const handleToggleWatchlist = async (ticker: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasWatchlistAccess) {
      navigate('/settings/subscription');
      return;
    }
    if (togglingTicker) return;
    setTogglingTicker(ticker);
    await toggleTicker(ticker);
    setTogglingTicker(null);
  };

  if (loading) {
    return (
      <div className="bg-[var(--color-bg-primary)] rounded-xl border border-[var(--color-border)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--color-border)]">
          <div className="h-6 w-40 skeleton rounded" />
        </div>
        <div className="divide-y divide-[var(--color-border)]">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="px-6 py-4 flex items-center gap-4">
              <div className="h-4 w-8 skeleton rounded" />
              <div className="h-4 w-20 skeleton rounded" />
              <div className="flex-1" />
              <div className="h-4 w-16 skeleton rounded" />
              <div className="h-4 w-16 skeleton rounded" />
              <div className="h-6 w-24 skeleton rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-bg-primary)] rounded-xl border border-[var(--color-border)] overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-[var(--color-border)] flex items-center gap-3">
        <div
          className={clsx(
            'h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0',
            type === 'buy'
              ? 'bg-emerald-500/10 text-emerald-500'
              : 'bg-red-500/10 text-red-500'
          )}
        >
          {type === 'buy' ? (
            <TrendingUp className="h-5 w-5" />
          ) : (
            <TrendingDown className="h-5 w-5" />
          )}
        </div>
        <div>
          <h3 className="font-semibold text-[var(--color-text-primary)]">{title}</h3>
          <p className="text-xs text-[var(--color-text-muted)]">
            {stocks.length} stocks found
          </p>
        </div>
      </div>

      {/* Stock Cards - Responsive */}
      <div className="divide-y divide-[var(--color-border)]">
        {sortedStocks.map((stock) => (
          <div
            key={stock.ticker}
            onClick={() => navigate(`/analysis?ticker=${stock.ticker}`)}
            className="p-4 sm:p-5 hover:bg-[var(--color-bg-secondary)] cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-medium text-[var(--color-text-muted)] w-6">
                  #{stock.rank}
                </span>
                <StockLogo ticker={stock.ticker} name={stock.name} website={stock.website} size="lg" />
                <div className="min-w-0">
                  <div className="font-semibold text-[var(--color-text-primary)]">
                    {stock.ticker}
                  </div>
                  <div className="text-xs text-[var(--color-text-muted)] truncate">
                    {stock.name}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <SignalBadge signal={stock.signal} />
                <button
                  onClick={(e) => handleToggleWatchlist(stock.ticker, e)}
                  disabled={togglingTicker === stock.ticker}
                  className={clsx(
                    'p-2 rounded-lg transition-colors cursor-pointer',
                    !hasWatchlistAccess
                      ? 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-secondary)]'
                      : isInWatchlist(stock.ticker)
                      ? 'text-yellow-500 bg-yellow-500/10'
                      : 'text-[var(--color-text-muted)] hover:text-yellow-500 hover:bg-yellow-500/10',
                    togglingTicker === stock.ticker && 'opacity-50 cursor-wait'
                  )}
                  title={
                    !hasWatchlistAccess
                      ? 'Upgrade to add to watchlist'
                      : isInWatchlist(stock.ticker)
                      ? 'Remove from watchlist'
                      : 'Add to watchlist'
                  }
                >
                  {togglingTicker === stock.ticker ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : !hasWatchlistAccess ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Star
                      className="h-4 w-4"
                      fill={isInWatchlist(stock.ticker) ? 'currentColor' : 'none'}
                    />
                  )}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-[var(--color-text-muted)] mb-1">Disconnect</p>
                <p
                  className={clsx(
                    'font-mono font-bold text-lg',
                    stock.disconnect_score >= 60
                      ? 'text-emerald-500'
                      : stock.disconnect_score <= 40
                      ? 'text-red-500'
                      : 'text-amber-500'
                  )}
                >
                  {formatNumber(stock.disconnect_score, 1)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--color-text-muted)] mb-1">Sentiment</p>
                <p
                  className={clsx(
                    'font-mono font-semibold',
                    stock.sentiment_score >= 60
                      ? 'text-emerald-500'
                      : stock.sentiment_score <= 40
                      ? 'text-red-500'
                      : 'text-amber-500'
                  )}
                >
                  {formatNumber(stock.sentiment_score, 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--color-text-muted)] mb-1">Fundamental</p>
                <p
                  className={clsx(
                    'font-mono font-semibold',
                    stock.fundamental_score >= 60
                      ? 'text-emerald-500'
                      : stock.fundamental_score <= 40
                      ? 'text-red-500'
                      : 'text-amber-500'
                  )}
                >
                  {formatNumber(stock.fundamental_score, 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--color-text-muted)] mb-1">Price</p>
                <p className="font-mono font-semibold text-[var(--color-text-primary)]">
                  {formatCurrency(stock.current_price)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
